import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "./ui/command";
import { api } from "../lib/api";
import { Building2, User, FolderKanban, Search } from "lucide-react";

export default function CommandPalette({ inline = false }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ clients: [], projects: [] });
  const nav = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      if (!q.trim()) { setResults({ clients: [], projects: [] }); return; }
      try {
        const r = await api.get("/search", { params: { q } });
        setResults(r.data);
      } catch {}
    }, 180);
    return () => clearTimeout(t);
  }, [q, open]);

  const go = useCallback((path) => { setOpen(false); setQ(""); nav(path); }, [nav]);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="open-cmdk"
        className={
          inline
            ? "flex items-center gap-2 border border-neutral-300 bg-white px-3 py-1.5 text-xs text-neutral-600 hover:border-neutral-400"
            : "fixed top-5 right-8 z-40 flex items-center gap-2 border border-neutral-300 bg-white px-3 py-1.5 text-xs text-neutral-600 hover:border-neutral-400 shadow-sm"
        }
      >
        <Search className="h-3.5 w-3.5" />
        <span>Cerca...</span>
        <kbd className="ml-2 font-mono text-[10px] px-1.5 py-0.5 bg-neutral-100 border border-neutral-200">⌘K</kbd>
      </button>
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Cerca clienti, gare, CIG, CUP..." value={q} onValueChange={setQ} data-testid="cmdk-input" />
        <CommandList>
          {q.trim() && !results.clients.length && !results.projects.length && (
            <CommandEmpty>Nessun risultato</CommandEmpty>
          )}
          {results.projects.length > 0 && (
            <CommandGroup heading="Gare & Progetti">
              {results.projects.map((p) => (
                <CommandItem key={p.id} onSelect={() => go(`/projects/${p.id}`)} value={`p-${p.id}-${p.title} ${p.cig || ""} ${p.cup || ""}`} data-testid={`cmdk-project-${p.id}`}>
                  <FolderKanban className="h-4 w-4 mr-2 opacity-60" />
                  <div className="flex-1 truncate">
                    <div className="text-sm truncate">{p.title}</div>
                    <div className="text-xs text-neutral-500 truncate">
                      {p.client_name} · {p.type === "pa" ? "PA" : "Privato"}
                      {p.cig && <span className="ml-2 font-mono">CIG {p.cig}</span>}
                      {p.cup && <span className="ml-2 font-mono">CUP {p.cup}</span>}
                    </div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {results.clients.length > 0 && (
            <CommandGroup heading="Clienti">
              {results.clients.map((c) => (
                <CommandItem key={c.id} onSelect={() => go(`/clients/${c.id}`)} value={`c-${c.id}-${c.name}`} data-testid={`cmdk-client-${c.id}`}>
                  {c.type === "pa" ? <Building2 className="h-4 w-4 mr-2 opacity-60" /> : <User className="h-4 w-4 mr-2 opacity-60" />}
                  <div className="flex-1 truncate">
                    <div className="text-sm truncate">{c.name}</div>
                    <div className="text-xs text-neutral-500 truncate">{c.city || (c.type === "pa" ? "Pubblica Amm." : "Privato")}</div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
}
